import { ksecGet, ksecPost, ksecPatch, ksecDelete } from '@/lib/ksec'
import type {
  KsecResponse,
  KsecListResponse,
  KsecMeContext,
  KsecEffectiveAccess,
  KsecValidateAccessRequest,
  KsecValidateAccessResult,
  KsecDigitalIdentity,
  KsecCreateIdentityRequest,
  KsecUpdateIdentityRequest,
  KsecIdentityAccess,
  KsecCreateCredentialRequest,
  KsecCredential,
  KsecRoleCatalog,
  KsecCreateRoleRequest,
  KsecUpdateRoleRequest,
  KsecCreateEntitlementRequest,
  KsecPermission,
  KsecCreatePermissionRequest,
  KsecPrivilege,
  KsecCreatePrivilegeRequest,
  KsecEntitlement,
  KsecDigitalAsset,
  KsecDigitalAssetItemDetail,
  KsecAttachment,
  KsecCreateAttachmentRequest,
  KsecPaginatedData,
  KsecSyncProductsResult,
  KsecUpdateOrgUserRequest,
  KsecUpdateOrgUserResult,
  KsecCreateOrgUserRequest,
  KsecCreateOrgUserResult,
  AccessAction,
} from './types'

// ─── Identity bootstrap (KSEC v3.0) ──────────────────────────────────────────
// Resolves DI.public_id from Auth0 sub claim. /by-email fue eliminado en v3.0.

/**
 * Paso 1 del bootstrap: resuelve la identidad KSEC usando el `sub` (user_id) de Auth0.
 * Respuesta: { data: KsecDigitalIdentity } — NO anidado en { data: { identity: ... } }.
 */
export function getIdentityByAuth0Sub(sub: string) {
  return ksecGet<KsecResponse<KsecDigitalIdentity>>(
    `/digital-identities/by-auth0-sub/${encodeURIComponent(sub)}`,
  )
}

// ─── Me / Context ─────────────────────────────────────────────────────────────

/**
 * @param identityId DI.public_id (v3.0). Se envía en header x-kbrm-party-id.
 */
export function getMeContext(identityId: string) {
  return ksecGet<KsecResponse<KsecMeContext>>('/me/context', {
    headers: { 'x-kbrm-party-id': identityId },
  })
}


// ─── Effective Access ─────────────────────────────────────────────────────────

/**
 * @param identityId DI.public_id (v3.0). El backend lo trata como identityPublicId.
 */
export function getEffectiveAccess(identityId: string) {
  return ksecGet<KsecResponse<KsecEffectiveAccess>>(
    `/auth/effective-access/${identityId}`,
    { headers: { 'x-kbrm-party-id': identityId } },
  )
}

// ─── Validate Access ──────────────────────────────────────────────────────────

export function validateAccess(payload: KsecValidateAccessRequest) {
  return ksecPost<KsecResponse<KsecValidateAccessResult>>(
    '/auth/validate-access',
    payload,
  )
}

// ─── Roles catalog ────────────────────────────────────────────────────────────

/**
 * Lista los roles disponibles filtrados por organización (businessTenantPublicId).
 * Sin este filtro se devuelven los roles de TODOS los tenants del sistema.
 */
export function getRolesCatalog(partyId: string, businessTenantPublicId?: string) {
  const params = new URLSearchParams({ size: '50' })
  if (businessTenantPublicId) params.set('businessTenantPublicId', businessTenantPublicId)
  return ksecGet<KsecResponse<KsecPaginatedData<KsecRoleCatalog> | KsecRoleCatalog[]>>(
    `/roles?${params.toString()}`,
    { headers: { 'x-kbrm-party-id': partyId } },
  )
}

// ─── Digital Identities ───────────────────────────────────────────────────────

export interface ListIdentitiesParams {
  page?: number
  size?: number
  status?: string
  partyPublicId?: string
  /** Scope filter: only return identities belonging to this organization. */
  organizationPartyPublicId?: string
  email?: string
}

export function listIdentities(params: ListIdentitiesParams = {}) {
  const { page = 1, size = 20, ...rest } = params
  // KSEC paginates from page 0; portal defaults to page 1 → subtract 1
  const query = new URLSearchParams({ page: String(Math.max(0, page - 1)), size: String(size) })
  if (rest.status) query.set('status', rest.status)
  if (rest.partyPublicId) query.set('partyPublicId', rest.partyPublicId)
  if (rest.organizationPartyPublicId) query.set('organizationPartyPublicId', rest.organizationPartyPublicId)
  if (rest.email) query.set('email', rest.email)
  return ksecGet<KsecListResponse<KsecDigitalIdentity>>(
    `/digital-identities?${query.toString()}`,
  )
}

export function getIdentity(identityId: string) {
  return ksecGet<KsecResponse<KsecDigitalIdentity>>(`/digital-identities/${identityId}`)
}

export function getIdentityAccess(identityId: string) {
  return ksecGet<KsecResponse<KsecIdentityAccess>>(
    `/digital-identities/${identityId}/access`,
  )
}

export function createIdentity(payload: KsecCreateIdentityRequest) {
  return ksecPost<KsecResponse<KsecDigitalIdentity>>('/digital-identities', payload)
}

export function updateIdentity(identityId: string, payload: KsecUpdateIdentityRequest) {
  return ksecPatch<KsecResponse<KsecDigitalIdentity>>(
    `/digital-identities/${identityId}`,
    payload,
  )
}

// ─── Credentials ─────────────────────────────────────────────────────────────

export function createCredential(identityId: string, payload: KsecCreateCredentialRequest) {
  return ksecPost<KsecResponse<KsecCredential>>(
    `/digital-identities/${identityId}/credentials`,
    payload,
  )
}

// ─── Attachments ─────────────────────────────────────────────────────────────

export function listAttachments(identityId: string) {
  return ksecGet<KsecResponse<KsecAttachment[]>>(
    `/digital-identities/${identityId}/attachments`,
  )
}

export function createAttachment(identityId: string, payload: KsecCreateAttachmentRequest) {
  return ksecPost<KsecResponse<KsecAttachment>>(
    `/digital-identities/${identityId}/attachments`,
    payload,
  )
}

// ─── Digital Assets catalog ───────────────────────────────────────────────────

export function listDigitalAssets(size = 50) {
  return ksecGet<KsecListResponse<KsecDigitalAsset>>(`/digital-assets?size=${size}`)
}

export function listDigitalAssetItems(assetId: string) {
  return ksecGet<KsecListResponse<KsecDigitalAssetItemDetail>>(
    `/digital-assets/${assetId}/items?size=100`,
  )
}

// ─── Roles CRUD ───────────────────────────────────────────────────────────────

export function createRole(payload: KsecCreateRoleRequest) {
  return ksecPost<KsecResponse<KsecRoleCatalog>>('/roles', payload)
}

export function updateRole(roleId: string, payload: KsecUpdateRoleRequest) {
  return ksecPatch<KsecResponse<KsecRoleCatalog>>(`/roles/${roleId}`, payload)
}

export function deleteRole(roleId: string) {
  return ksecDelete<KsecResponse<{ id: string; deleted: boolean }>>('/roles', {
    params: { public_id: roleId },
  })
}

// ─── Entitlements ─────────────────────────────────────────────────────────────

export function listRoleEntitlements(roleId: string) {
  return ksecGet<KsecResponse<KsecEntitlement[]>>(`/roles/${roleId}/entitlements`)
}

export function createEntitlement(roleId: string, payload: KsecCreateEntitlementRequest) {
  return ksecPost<KsecResponse<KsecEntitlement>>(`/roles/${roleId}/entitlements`, payload)
}

export function deleteEntitlement(roleId: string, entitlementId: string) {
  return ksecDelete<KsecResponse<{ id: string; deleted: boolean }>>(
    `/roles/${roleId}/entitlements/${entitlementId}`,
  )
}

// ─── Permissions ─────────────────────────────────────────────────────────────

export function listPermissions(
  userPublicId: string,
  onlyActive = true,
  businessTenantPublicId?: string,
) {
  const params = new URLSearchParams({
    userPublicId,
    onlyActive: String(onlyActive),
  })
  if (businessTenantPublicId) params.set('businessTenantPublicId', businessTenantPublicId)
  return ksecGet<KsecListResponse<KsecPermission>>(`/permissions?${params.toString()}`)
}

/**
 * Lista todos los permisos de una organización (scope = businessTenantPublicId).
 * Usar para determinar qué usuarios pertenecen a la org y filtrar identidades.
 */
export function listOrgPermissions(businessTenantPublicId: string, onlyActive = true) {
  return ksecGet<KsecListResponse<KsecPermission>>(
    `/permissions?businessTenantPublicId=${businessTenantPublicId}&onlyActive=${onlyActive}&size=200`,
  )
}

export function createPermission(payload: KsecCreatePermissionRequest) {
  return ksecPost<KsecResponse<KsecPermission>>('/permissions', payload)
}

export function revokePermission(permissionId: string) {
  return ksecDelete<KsecResponse<KsecPermission>>(`/permissions/${permissionId}`)
}

// ─── Privileges ───────────────────────────────────────────────────────────────

export function listPrivileges(permissionId: string) {
  return ksecGet<KsecResponse<KsecPrivilege[]>>(`/permissions/${permissionId}/privileges`)
}

export function createPrivilege(permissionId: string, payload: KsecCreatePrivilegeRequest) {
  return ksecPost<KsecResponse<KsecPrivilege>>(
    `/permissions/${permissionId}/privileges`,
    payload,
  )
}

export function deletePrivilege(permissionId: string, privilegeId: string) {
  return ksecDelete<KsecResponse<{ id: string; deleted: boolean }>>(
    `/permissions/${permissionId}/privileges/${privilegeId}`,
  )
}

export function addRoleToPermission(permissionId: string, roleId: string) {
  return ksecPost<KsecResponse<KsecPermission>>(
    `/permissions/${permissionId}/asset-user-roles`,
    { roleId },
  )
}

export function revokeAssetUserRole(permissionId: string, aurId: string) {
  return ksecDelete<KsecResponse<unknown>>(
    `/permissions/${permissionId}/asset-user-roles/${aurId}`,
  )
}

// ─── Organization User Update (EP1-UPDATE) ────────────────────────────────────

/**
 * Actualiza datos de un usuario organizacional existente.
 * PATCH /create-users/organizations/:individualPublicId
 *
 * Todos los campos del payload son opcionales; el servidor actualiza solo los enviados.
 * `firstName` y `lastName` deben ir juntos. `rol` requiere también `party_id`.
 *
 * @param individualPublicId public_id del Individual en KBRM (ind_xxx).
 *   Se obtiene de `identity.individualPublicId` en el store (cargado en bootstrap).
 */
export function updateOrgUser(
  individualPublicId: string,
  payload: KsecUpdateOrgUserRequest,
) {
  return ksecPatch<KsecResponse<KsecUpdateOrgUserResult>>(
    `/create-users/organizations/${encodeURIComponent(individualPublicId)}`,
    payload,
  )
}

// ─── Organization User Create (EP-BOOTSTRAP) ─────────────────────────────────

/**
 * Crea un nuevo usuario organizacional via el endpoint de bootstrap.
 *
 * Flujo interno del backend:
 *   1. POST KBRM /individuals — crea el individuo en KBRM (bloqueante).
 *   2. Solo si KBRM responde 200 → crea el usuario en KSEC (DigitalIdentity,
 *      FederatedCredential, Permission, AssetUserRole).
 *   3. Si el rol es ADMIN → crea Entitlements automáticamente (best-effort).
 *
 * @param payload - Datos del nuevo usuario. `partyRolePublicId` es opcional:
 *   si se omite, el backend lo deriva del party_role devuelto por KBRM.
 */
export function createOrgUser(payload: KsecCreateOrgUserRequest) {
  return ksecPost<KsecResponse<KsecCreateOrgUserResult>>(
    '/create-users/organizations',
    payload,
  )
}

// ─── Party Role Sync (EP3) ────────────────────────────────────────────────────

/**
 * Consulta los productos de la organizacion en KBRM. Operacion de solo lectura:
 * no crea ni modifica registros en BD.
 *
 * Llama a POST /party-roles/:partyRolePublicId/sync-products en KSEC.
 * El backend consulta KBRM GET /get-product-by-organization/{organizationPublicId},
 * resuelve los DigitalAssets por product_code y devuelve los productos encontrados.
 *
 * @param partyRolePublicId    public_id del PartyRole en KBRM
 * @param organizationPublicId public_id de la organizacion en KBRM (ej: org_xxx)
 */
export function syncPartyRoleProducts(partyRolePublicId: string, organizationPublicId: string) {
  return ksecPost<KsecResponse<KsecSyncProductsResult>>(
    `/party-roles/${encodeURIComponent(partyRolePublicId)}/sync-products`,
    { organizationPublicId },
  )
}

// ─── Guard helper ─────────────────────────────────────────────────────────────

/**
 * Server-side guard (v3.0).
 * @param identityId DI.public_id del usuario evaluado.
 * Always returns HTTP 200; real result is in data.allowed.
 */
export async function guardAction(
  identityId: string,
  tagCode: string,
  action: AccessAction,
): Promise<boolean> {
  const res = await validateAccess({ identityPublicId: identityId, tagCode, action })
  return res.data.allowed === true
}